import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { AutoAwesome, Download, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

const MAROON = '#7b0011';

interface DailyRecommendation {
  id: number;
  rank: number;
  status: string;
  score: number;
  reason: string;
  recipient_name: string;
  recipient_profile_id?: string;
  recommended_name: string;
  recommended_profile_id?: string;
}

interface DailyRecommendationReport {
  match_date: string;
  total_recommendations: number;
  recipients_with_matches: number;
  eligible_members: number;
  run?: { status: string; started_at: string; completed_at?: string | null; batch_size: number; run_attempts: number; notifications_created: number; failed_recipients: number; error_summary?: string | null } | null;
  items: DailyRecommendation[];
}

interface DailyMatchRun {
  run_date: string;
  status: string;
  run_attempts: number;
  recipients_evaluated: number;
  matches_created: number;
  notifications_created: number;
  failed_recipients: number;
}

const AdminDailyRecommendationsPage: React.FC = () => {
  const [report, setReport] = useState<DailyRecommendationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [runs, setRuns] = useState<DailyMatchRun[]>([]);

  const loadReport = async (reportDate = selectedDate) => {
    try {
      setLoading(true);
      const [response, runsResponse] = await Promise.all([
        api.get<DailyRecommendationReport>('/daily-matches/admin/history', { params: { match_date: reportDate } }),
        api.get<{ items: DailyMatchRun[] }>('/daily-matches/admin/runs'),
      ]);
      setReport(response.data);
      setRuns(runsResponse.data.items || []);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load daily recommendation report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadReport(); }, []);

  const generateToday = async () => {
    try {
      setGenerating(true);
      const response = await api.post('/daily-matches/admin/generate');
      toast.success(`${response.data.matches_created || 0} daily recommendations generated`);
      await loadReport(selectedDate);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to generate daily recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get('/daily-matches/admin/today/export', { params: { match_date: selectedDate }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily-recommendations-${report?.match_date || 'today'}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to download recommendation report');
    }
  };

  return (
    <AdminLayout currentPage="daily-recommendations">
      <Paper sx={{ borderRadius: 3, border: '1px solid #ead7be', p: { xs: 2, md: 3 }, mb: 2, background: 'linear-gradient(135deg, #fffaf3, #fff 58%, #fff0dc)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box><Typography variant="h4" sx={{ color: MAROON, fontWeight: 900 }}>Daily Recommendations</Typography><Typography sx={{ color: '#766860', mt: 0.6 }}>Review automated profile recommendations and run a recovery generation when required.</Typography></Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}><Button variant="outlined" startIcon={<Refresh />} onClick={() => loadReport()} disabled={loading} sx={{ borderColor: MAROON, color: MAROON }}>Refresh</Button><Button variant="outlined" startIcon={<Download />} onClick={downloadReport} disabled={!report?.items.length} sx={{ borderColor: MAROON, color: MAROON }}>Download Excel</Button><Button variant="contained" startIcon={<AutoAwesome />} onClick={generateToday} disabled={generating} sx={{ backgroundColor: MAROON, '&:hover': { backgroundColor: '#5f000e' } }}>{generating ? 'Generating...' : 'Generate Today'}</Button></Box>
        </Box>
      </Paper>
      <Paper sx={{ borderRadius: 2, border: '1px solid #ead7be', p: 1.5, mb: 2, display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ color: MAROON, fontWeight: 800 }}>Recommendation History</Typography>
        <TextField size="small" type="date" label="Report date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" onClick={() => loadReport(selectedDate)} sx={{ backgroundColor: MAROON, '&:hover': { backgroundColor: '#5f000e' } }}>View Date</Button>
        <Typography sx={{ color: '#766860', fontSize: '0.85rem' }}>The daily job is automatic. These controls only review history or run a recovery generation.</Typography>
      </Paper>
      {loading ? <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress sx={{ color: MAROON }} /></Box> : <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 2 }}>
          {[['Date', report?.match_date || '-'], ['Eligible members', report?.eligible_members || 0], ['Members receiving matches', report?.recipients_with_matches || 0], ['Recommendations generated', report?.total_recommendations || 0]].map(([label, value]) => <Card key={String(label)} sx={{ border: '1px solid #ead7be', borderRadius: 2 }}><CardContent sx={{ textAlign: 'center' }}><Typography sx={{ color: '#7f7167', fontSize: '0.85rem', fontWeight: 700 }}>{label}</Typography><Typography sx={{ color: MAROON, fontSize: '1.45rem', fontWeight: 900, mt: 0.4 }}>{value}</Typography></CardContent></Card>)}
        </Box>
        {report?.run && <Alert severity={report.run.status === 'COMPLETED' ? 'success' : report.run.status === 'FAILED' ? 'error' : 'info'} sx={{ mb: 2 }}>Run status: {report.run.status}. Attempts: {report.run.run_attempts}. Notifications created: {report.run.notifications_created}. Failed recipients: {report.run.failed_recipients}.{report.run.error_summary ? ` Error: ${report.run.error_summary}` : ''}</Alert>}
        {!report?.items.length ? <Alert severity="info">No recommendations have been generated today. {report?.eligible_members ? 'Eligible members do not currently have matching candidates.' : 'No active member has saved Match Preferences yet.'}</Alert> : <TableContainer component={Paper} sx={{ border: '1px solid #ead7be', borderRadius: 2 }}><Table size="small"><TableHead><TableRow sx={{ backgroundColor: '#fff7eb' }}><TableCell>Recipient</TableCell><TableCell>Recommended Profile</TableCell><TableCell>Rank</TableCell><TableCell>Score</TableCell><TableCell>Reason</TableCell><TableCell>Status</TableCell></TableRow></TableHead><TableBody>{report.items.map((item) => <TableRow key={item.id}><TableCell><Typography sx={{ fontWeight: 700 }}>{item.recipient_name}</Typography><Typography variant="caption" color="text.secondary">{item.recipient_profile_id || '-'}</Typography></TableCell><TableCell><Typography sx={{ fontWeight: 700 }}>{item.recommended_name}</Typography><Typography variant="caption" color="text.secondary">{item.recommended_profile_id || '-'}</Typography></TableCell><TableCell>{item.rank}</TableCell><TableCell>{Math.round(item.score)}</TableCell><TableCell>{item.reason}</TableCell><TableCell>{item.status}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}
        {runs.length > 0 && <Paper sx={{ mt: 2.2, border: '1px solid #ead7be', borderRadius: 2, overflow: 'hidden' }}><Box sx={{ p: 1.5, backgroundColor: '#fff7eb' }}><Typography sx={{ color: MAROON, fontWeight: 900 }}>Recent Automated Runs</Typography></Box><TableContainer><Table size="small"><TableHead><TableRow><TableCell>Date</TableCell><TableCell>Status</TableCell><TableCell>Evaluated</TableCell><TableCell>Recommendations</TableCell><TableCell>Notifications</TableCell><TableCell>Failures</TableCell><TableCell>Attempts</TableCell></TableRow></TableHead><TableBody>{runs.map((run) => <TableRow key={run.run_date}><TableCell>{run.run_date}</TableCell><TableCell>{run.status}</TableCell><TableCell>{run.recipients_evaluated}</TableCell><TableCell>{run.matches_created}</TableCell><TableCell>{run.notifications_created}</TableCell><TableCell>{run.failed_recipients}</TableCell><TableCell>{run.run_attempts}</TableCell></TableRow>)}</TableBody></Table></TableContainer></Paper>}
      </>}
    </AdminLayout>
  );
};

export default AdminDailyRecommendationsPage;